<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import type { AppContext } from '../app';
  import { cue } from '../audio/sound';
  import { lang, t } from '../i18n';
  import { get } from 'svelte/store';
  import { markIntroSeen, motionReduced } from '../state/settings';
  import { introActive } from '../state/stores';

  // Intro (S2-12): the launch title appears at once (small, so it is not the LCP element), the scene holds in the
  // dark until it is warm, then the Sun ignites and the camera sweeps in, then the HUD slides in (S3-02). Tap, click
  // or any key skips it. With reduced motion there is no camera sweep, only the title.
  let { ctx }: { ctx: AppContext } = $props();

  /** From the end of the dark hold: the sweep (4.8 s) plus a moment to read the title. */
  const END_MS = 5000;
  const END_REDUCED_MS = 2400;

  let showTitle = $state(false);
  let timers: ReturnType<typeof setTimeout>[] = [];
  let finished = false;

  function finish(skipped: boolean): void {
    if (finished) return;
    finished = true;
    for (const id of timers) clearTimeout(id);
    if (skipped) ctx.scene.skipIntro();
    markIntroSeen();
    introActive.set(false);
  }

  onMount(() => {
    const reduced = get(motionReduced);
    showTitle = true;
    void ctx.scene.playIntro(reduced).then(() => {
      if (finished) return;
      cue('rumble');
      timers.push(setTimeout(() => finish(false), reduced ? END_REDUCED_MS : END_MS));
    });
  });
  onDestroy(() => {
    for (const id of timers) clearTimeout(id);
  });

  // the title names the opening date and what is in flight then (S4-25: today — Voyager 1, Voyager 2, New Horizons)
  const dateText = $derived(
    new Intl.DateTimeFormat($lang === 'de' ? 'de-DE' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(ctx.start.time),
  );
  const count = $derived(ctx.start.inFlight.length);
  const names = $derived(ctx.start.inFlight.map((id) => ctx.targets.get(id)?.name[$lang] ?? id).join(' · '));
</script>

<svelte:window onkeydown={() => finish(true)} />

<!-- the whole screen skips the intro on tap; the button is the accessible way to do the same -->
<div class="intro" role="presentation" onpointerdown={() => finish(true)} data-testid="intro">
  {#if showTitle}
    <div class="title" role="status">
      <div class="date">{dateText}</div>
      {#if count > 0}
        <div class="craft">
          {count === 1 ? $t('intro.inFlightOne') : $t('intro.inFlight', { n: String(count) })}
        </div>
        <div class="event">{names}</div>
      {/if}
    </div>
  {/if}
  <button type="button" class="skip" onclick={() => finish(true)}>{$t('intro.skip')}</button>
</div>

<style>
  .intro {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: end center;
    padding: 0 16px calc(env(safe-area-inset-bottom) + 18vh);
  }
  .title {
    text-align: center;
    animation: title-in 900ms var(--ease);
  }
  .date {
    font-family: var(--font-mono);
    font-size: 0.95rem;
    color: var(--accent);
    letter-spacing: 0.08em;
  }
  .craft {
    margin-top: 6px;
    font-size: clamp(1.1rem, 4.4vw, 2rem);
    font-weight: 500;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    text-shadow: 0 0 18px var(--scrim);
  }
  .event {
    margin-top: 4px;
    color: var(--fg-muted);
    font-size: 1rem;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
  }
  .skip {
    position: fixed;
    right: calc(env(safe-area-inset-right) + 16px);
    bottom: calc(env(safe-area-inset-bottom) + 16px);
    min-height: var(--touch);
    padding: 0 16px;
    border: var(--rule-w) solid var(--rule);
    background: var(--panel);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
  }
  @keyframes title-in {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .title {
      animation: none;
    }
  }
</style>
